<template>
  <div class="job-post-search-results">
    <div
      v-if="loading"
      class="d-flex justify-center pa-4"
    >
      <v-progress-circular
        indeterminate
        color="brandPrimary"
        size="32"
        width="2"
      />
    </div>

    <div
      v-else-if="results.length === 0 && hasSearched"
      class="text-body-2 text-textLight text-center pa-4"
    >
      No matching job posts found.
    </div>

    <div
      v-else-if="results.length === 0"
      class="text-body-2 text-textLight text-center pa-4"
    >
      Start typing to search for similar job posts.
    </div>

    <div
      v-else
      class="pa-0"
      role="list"
      :aria-labelledby="ariaLabelledby"
    >
      <div
        v-for="result in results"
        :key="result.id"
        role="listitem"
        class="job-post-search-result-item pa-3 ma-2 rounded-lg d-flex align-center ga-3 cursor-pointer"
        :class="{ 'border border-primary': selectedId === result.id }"
        :aria-current="selectedId === result.id ? 'true' : undefined"
        @click="$emit('select', selectedId === result.id ? null : result)"
      >
        <div class="score-meter">
          <ScoreMeter
            :score="result.score_total_capped"
            icon
          />
        </div>

        <!-- Text content -->
        <div class="flex-grow-1 min-width-0">
          <div class="text-h3 text-textDarkest">
            {{ result.title }}
          </div>

          <div class="text-body-3 opacity-100 mt-1">
            <span
              v-if="result.flexible_id"
              class="text-textLight ml-2"
            >
              {{ result.flexible_id }} -
            </span>
            <span
              v-if="result.location"
              class="text-textLight"
            >
              {{ result.location }}
            </span>
          </div>

          <div class="mt-2 d-flex flex-wrap ga-2 align-center justify-space-between">
            <JobStatusPill
              :status-text="statusText(result)"
              :status-class="statusClass(result)"
            />

            <v-btn
              v-if="selectedId === result.id"
              class="view-in-editor-btn"
              variant="outlined"
              color="primary"
              size="small"
              rounded="lg"
              :to="{ name: 'editor-view', params: { id: result.id } }"
              @click.stop
            >
              View in Editor
            </v-btn>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import ScoreMeter from '@/components/editor/ScoreMeter.vue';
import JobStatusPill from '@/components/editor/JobStatusPill.vue';

defineProps({
  results: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  selectedId: {
    type: [Number, String],
    default: null,
  },
  hasSearched: {
    type: Boolean,
    default: false,
  },
  ariaLabelledby: {
    type: String,
    default: 'similar-jobs-heading',
  },
});

defineEmits(['select']);

const statusText = (result) => {
  if (result.is_flagship) return 'Flagship';
  if (result.status === 'Open') return 'Published';
  return result.status;
};

const statusClass = (result) => {
  if (result.is_flagship) return 'flagship';
  switch (result.status) {
    case 'Open':
    case 'Published': return 'open';
    case 'Unpublished': return 'unpublished';
    default: return 'draft';
  }
};
</script>

<style scoped lang="less">
.job-post-search-result-item {
  transition: background-color 0.1s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
}

.min-width-0 {
  min-width: 0;
}

.view-in-editor-btn {
  background-color: @bg-default !important;

  &:hover,
  &:focus,
  &:active,
  &:focus-visible {
    background-color: @bg-default !important;
  }

  :deep(.v-btn__overlay) {
    opacity: 0 !important;
  }
}

.score-meter {
  width: 40px;
  min-width: 40px;
  height: 40px;
  min-height: 40px;
}
</style>
